import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InputNumber, Tooltip } from 'antd';
import { Pencil, UserPlus } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import { addDays, deptOf, planGrid, positionById, toDay } from './hrLogic';
import { PageHeader, Panel, reqUrl, useHrPerms } from './hrUi';
import { RequisitionModal } from './hrModals';

/** Định biên: số người cần có theo chi nhánh × bộ phận, so với thực tế và yêu cầu đang tuyển */
export default function HeadcountPlan() {
  const s = useHr();
  const navigate = useNavigate();
  const { canEdit, canRecruit } = useHrPerms();
  const today = toDay();
  const [edit, setEdit] = useState(false);
  const [reqInit, setReqInit] = useState<Record<string, unknown> | null>(null);
  const grid = planGrid(s, today);
  const depts = s.depts.filter(d => grid.some(r => r.cells.some(c => c.dept.id === d.id && (c.plan || c.actual))) || edit);
  const total = (fn: (c: (typeof grid)[number]['cells'][number]) => number, branchId?: string) =>
    grid.filter(r => !branchId || r.branch.id === branchId).reduce((a, r) => a + r.cells.reduce((x, c) => x + fn(c), 0), 0);
  const gaps = grid.flatMap(r => r.cells.filter(c => c.uncovered > 0).map(c => ({ branch: r.branch, ...c })));

  const createReq = (branchId: string, deptId: string, qty: number) => {
    const leaving = s.employees.filter(e => e.branchId === branchId && deptOf(s, e) === deptId && e.leftDate && e.leftDate >= addDays(today, -60) && e.leaveType !== 'probation_fail');
    const positionId = leaving[0]?.positionId || s.positions.find(p => p.deptId === deptId && p.level === 'staff')?.id;
    const pos = positionById(s, positionId);
    setReqInit({ branchId, positionId, qty, reason: leaving.length ? 'replace' : 'expand', replaceEmpIds: leaving.slice(0, qty).map(e => e.id), salaryMin: pos?.salaryMin, salaryMax: pos?.salaryMax });
  };

  const cell = (c: (typeof grid)[number]['cells'][number], branchId: string) => {
    const diff = c.actual - c.leaving - c.plan;
    const tone = !c.plan && !c.actual ? 'text-gray-300' : diff < 0 ? (c.uncovered ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700') : diff > 0 ? 'bg-violet-50 text-violet-700' : 'bg-emerald-50 text-emerald-700';
    return (
      <td key={c.dept.id + branchId} className="p-1.5 border-t border-gray-100 dark:border-gray-800 text-center align-top">
        {!edit && !c.plan && !c.actual ? <span className="text-gray-300">—</span> : edit ? (
          <InputNumber size="small" min={0} max={99} value={c.plan} className="!w-[64px]" onChange={v => hrApi.setPlan(branchId, c.dept.id, Number(v) || 0)} />
        ) : (
          <Tooltip title={`Định biên ${c.plan} · đang làm ${c.actual}${c.leaving ? ` (${c.leaving} sắp nghỉ)` : ''}${c.recruiting ? ` · đang tuyển ${c.recruiting}` : ''}${c.upcoming ? ` · ${c.upcoming} sắp vào` : ''}`}>
            <div className={`rounded-lg px-2 py-1.5 ${tone}`}>
              <div className="font-semibold text-sm">{c.actual}<span className="font-normal opacity-60">/{c.plan}</span></div>
              <div className="text-[10px] leading-tight space-x-1">
                {c.leaving > 0 && <span>−{c.leaving} sắp nghỉ</span>}
                {c.recruiting > 0 && <span>+{c.recruiting} đang tuyển</span>}
                {diff > 0 && <span>vượt {diff}</span>}
              </div>
            </div>
          </Tooltip>
        )}
      </td>
    );
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Định biên nhân sự" desc="Mỗi ô: đang làm / định biên. Tuyển vượt định biên cần Ban giám đốc duyệt."
          extra={canEdit && <Button icon={<Pencil size={14} />} type={edit ? 'primary' : 'default'} onClick={() => setEdit(!edit)}>{edit ? 'Xong' : 'Chỉnh định biên'}</Button>} />
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left font-medium p-3">Bộ phận</th>
                {grid.map(r => <th key={r.branch.id} className="font-medium p-3">{r.branch.short}</th>)}
                <th className="font-medium p-3">Tổng</th>
              </tr>
            </thead>
            <tbody>
              {depts.map(d => (
                <tr key={d.id}>
                  <td className="p-3 border-t border-gray-100 dark:border-gray-800 whitespace-nowrap"><span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: d.color }} />{d.name}</td>
                  {grid.map(r => cell(r.cells.find(c => c.dept.id === d.id)!, r.branch.id))}
                  <td className="p-3 border-t border-gray-100 dark:border-gray-800 text-center font-medium">
                    {grid.reduce((a, r) => a + (r.cells.find(c => c.dept.id === d.id)?.actual || 0), 0)}/{grid.reduce((a, r) => a + (r.cells.find(c => c.dept.id === d.id)?.plan || 0), 0)}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                <td className="p-3">Tổng</td>
                {grid.map(r => <td key={r.branch.id} className="p-3 text-center">{total(c => c.actual, r.branch.id)}/{total(c => c.plan, r.branch.id)}</td>)}
                <td className="p-3 text-center">{total(c => c.actual)}/{total(c => c.plan)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
          <span><span className="inline-block w-3 h-3 rounded bg-emerald-100 align-middle mr-1" />Đủ</span>
          <span><span className="inline-block w-3 h-3 rounded bg-amber-100 align-middle mr-1" />Thiếu, đã có yêu cầu tuyển</span>
          <span><span className="inline-block w-3 h-3 rounded bg-rose-100 align-middle mr-1" />Thiếu, chưa có kế hoạch tuyển</span>
          <span><span className="inline-block w-3 h-3 rounded bg-violet-100 align-middle mr-1" />Vượt định biên</span>
        </div>

        <Panel title={`Thiếu người chưa có yêu cầu tuyển (${gaps.length})`}>
          {gaps.length === 0 ? <div className="p-4 text-sm text-gray-400">Các vị trí thiếu đều đã có yêu cầu tuyển dụng.</div> : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {gaps.map(g => (
                <div key={g.branch.id + g.dept.id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="flex-1 text-sm"><b className="font-medium">{g.dept.name} · {g.branch.short}</b> <span className="text-gray-500">thiếu {g.uncovered} (định biên {g.plan}, đang làm {g.actual - g.leaving}{g.recruiting ? `, đang tuyển ${g.recruiting}` : ''})</span></div>
                  {canRecruit && <Button size="small" type="primary" icon={<UserPlus size={13} />} onClick={() => createReq(g.branch.id, g.dept.id, g.uncovered)}>Tạo yêu cầu</Button>}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
      {reqInit && <RequisitionModal init={reqInit} onClose={() => setReqInit(null)} onSaved={id => navigate(reqUrl(id))} />}
    </Layout>
  );
}
